import { mergeStyles } from "@fluentui/react";
import { Dropdown, Option, OptionOnSelectData, SelectionEvents, useId } from "@fluentui/react-components";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomDropdownProps } from "./ProductView.interfaces";
import { PRODUCT_VIEW_STYLES } from "./ProductView.styles";

const CustomDropdown = ({
  label,
  data,
  defaultOption,
  onSelectedOption,
  styles,
  isMultiSelect,
  listboxMaxHeight,
  dropdownWidth
}: CustomDropdownProps) => {
  const dropdownId = useId("dropdown-default-select-option");
  const [selectedOptions, setSelectedOptions] = useState<string[]>([]);
  const [selectedValue, setSelectedValue] = useState<string | undefined>("");

  const handleSelectedOption = useCallback((option?: OptionOnSelectData) => {
    if (option?.optionValue) {
      setSelectedOptions(option.selectedOptions);
    } else {
      setSelectedOptions([]);
    }
    setSelectedValue(option?.optionText);
  }, []);

  const onMetricSelect = (_event: SelectionEvents, data: OptionOnSelectData) => {
    handleSelectedOption(data);
    if (onSelectedOption) {
      onSelectedOption(data);
    }
  };

  useEffect(() => {
    handleSelectedOption(defaultOption);
  }, [defaultOption]);

  const isMultipleSelections = isMultiSelect && selectedOptions.length > 1;

  const dropdownWrapperClass = useMemo(
    () =>
      mergeStyles({
        width: "100%",
        "& button, & [role='combobox']": {
          fontStyle: "italic"
        }
      }),
    []
  );

  // Rich listbox styling with scroll
  const listboxStyles = useMemo(
    () =>
      listboxMaxHeight
        ? {
          className: mergeStyles({
            maxHeight: `${listboxMaxHeight}px !important`,
            overflowY: "auto !important" as any,
            borderRadius: "8px",
            boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)",
            border: "1px solid rgba(0, 0, 0, 0.08)",
            padding: "4px",
            backgroundColor: "#ffffff",
            // Custom scrollbar styling
            "::-webkit-scrollbar": {
              width: "6px"
            },
            "::-webkit-scrollbar-track": {
              background: "#f1f1f1",
              borderRadius: "3px"
            },
            "::-webkit-scrollbar-thumb": {
              background: "#c1c1c1",
              borderRadius: "3px"
            },
            "::-webkit-scrollbar-thumb:hover": {
              background: "#a1a1a1"
            }
          })
        }
        : undefined,
    [listboxMaxHeight]
  );

  return (
    <div
      className={`${PRODUCT_VIEW_STYLES.TAB.COHORTS.CLASS_NAMES.viewByMetricSelectContainer} ${mergeStyles(styles?.root)}`}
    >
      <label htmlFor={`${dropdownId}-controlled`} style={{ whiteSpace: "nowrap" }}>
        {label?.trim() ? label : "View By"}:
      </label>
      <div className={isMultipleSelections ? dropdownWrapperClass : mergeStyles({ width: "100%" })}>
        <Dropdown
          aria-labelledby={dropdownId}
          id={`${dropdownId}-controlled`}
          onOptionSelect={onMetricSelect}
          placeholder="--Select--"
          multiselect={!!isMultiSelect}
          selectedOptions={selectedOptions}
          value={isMultipleSelections ? "Multiple Selections" : selectedValue}
          style={{
            width: dropdownWidth ?? 180
          }}
          listbox={listboxStyles}
        >
          {(data || []).map((option) => (
            <Option text={option.text} value={option.value} key={`${dropdownId}-${option.value}`}>
              {option.text || ""}
            </Option>
          ))}
        </Dropdown>
      </div>
    </div>
  );
};

export default CustomDropdown;
